import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../styles/intake-history.scss";
import OrangeSeperator from "./OrangeSeperator";
import { setIntakeHistory } from "../redux/actions/nutritionActions";
import { setIntakeHistoryHeight } from "../redux/actions/heightActions";
import { setIntakeHistoryDemo } from "../redux/actions/demoActions";
import IntakeHistoryInput from "./sub_components/IntakeHistoryInput";
import IntakeHistoryItem from "./sub_components/IntakeHistoryItem";
import { useWindowWidth } from "../hooks/useWindowWidth";

const IntakeHistory = () => {
  const todays_intake = useSelector((state) => state.nutritionReducer.todays_intake);
  const todays_intake_demo = useSelector((state) => state.demoReducer.todays_intake_demo);
  const intake_history = useSelector((state) => state.nutritionReducer.intake_history);
  const intake_history_demo = useSelector((state) => state.demoReducer.intake_history_demo);
  const hero_height = useSelector((state) => state.heightReducer.hero_height);
  const modifyIntake_height = useSelector((state) => state.heightReducer.modifyIntake_height);
  const demo = useSelector((state) => state.demoReducer.demo);
  const activeList = demo ? todays_intake_demo : todays_intake;
  const activeIntakeHistoryList = demo ? intake_history_demo : intake_history;
  const [errorMessage, setErrorMessage] = useState(false);
  const dispatch = useDispatch();
  const intakeHistoryRef = useRef();
  const [showCustomItem, setShowCustomItem] = useState(false);
  const [searchBarValue, setSearchBarValue] = useState("");
  const [searchedData, setSearchedData] = useState([]);
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const dateRef = useRef(null);
  const amountRef = useRef(null);
  const caloriesRef = useRef(null);
  const fatRef = useRef(null);
  const proteinRef = useRef(null);
  const carbsRef = useRef(null);
  const windowWidth = useWindowWidth();

  // Resets form on demo change
  useEffect(() => {
    handleFormReset();
  }, [demo]);

  // Gets intake history height and sends it to the redux store
  useEffect(() => {
    dispatch(
      setIntakeHistoryHeight(
        intakeHistoryRef.current != null && intakeHistoryRef.current.getBoundingClientRect().height
      )
    );
  }, [dispatch, searchedData, windowWidth]);

  // Displays searched items based on condition
  useEffect(() => {
    if (searchBarValue === "") {
      setSearchedData(activeIntakeHistoryList);
    } else {
      setSearchedData(activeIntakeHistoryList.filter((item) => item.date.match(searchBarValue)));
    }
  }, [activeIntakeHistoryList, searchBarValue]);

  // Gets intake_history from the local storage and sets it to the intake_history from the redux store
  useEffect(() => {
    const ls = localStorage.getItem("intake_history");
    if (ls) dispatch(setIntakeHistory(JSON.parse(ls)));
  }, [dispatch]);

  // Saves intake_history to the local storage
  useEffect(() => {
    localStorage.setItem("intake_history", JSON.stringify(intake_history));
  }, [intake_history]);

  // Handles form resetting
  const handleFormReset = () => {
    setErrorMessage(false);
    setDate("");
    setAmount("");
    setCalories("");
    setFat("");
    setProtein("");
    setCarbs("");
  };

  // Handles intake submit
  const handleSubmit = () => {
    if (!date || !amount || !calories || !fat || !protein || !carbs) {
      setErrorMessage(true);
    } else {
      let customItemInput = { calories, amount, carbs, fat, protein };
      for (const key in customItemInput) {
        if (!customItemInput[key].includes("/")) {
          customItemInput[key] = JSON.parse(customItemInput[key]);
        }
      }
      customItemInput.date = date;
      customItemInput.id = Math.random();
      if (activeList === todays_intake) {
        dispatch(setIntakeHistory([...intake_history, customItemInput]));
      } else {
        dispatch(setIntakeHistoryDemo([...intake_history_demo, customItemInput]));
      }
      handleFormReset();
    }
  };

  // Handle item remove
  const handleDelete = (id) => {
    let filteredList = activeIntakeHistoryList.filter((item) => item.id !== id);
    if (activeList === todays_intake) {
      dispatch(setIntakeHistory(filteredList));
    } else {
      dispatch(setIntakeHistoryDemo(filteredList));
    }
  };

  // Handles search bar
  const handleSearch = (e) => {
    if (/^[0-9/]*$/g.test(e.target.value)) {
      setSearchBarValue(e.target.value);
      setSearchedData(activeIntakeHistoryList.filter((item) => item.date.match(e.target.value)));
    }
  };

  return (
    <div className="intake-history" ref={intakeHistoryRef}>
      <header className="intake-history__header">
        <h3 className="intake-history__title">Intake History</h3>
        <OrangeSeperator />
        <p className="intake-history__description">
          Here you can see all of your submissions from the{" "}
          <span
            className="intake-history__highlighted"
            onClick={() => {
              window.scrollTo(0, hero_height + modifyIntake_height);
            }}
          >
            “INTAKE” section
          </span>
          . You also have an option to add a custom intake.
        </p>
      </header>
      <div className="intake-history__content">
        <div className="intake-history__container">
          <input
            className="intake-history__search"
            placeholder="Search for a date (MM/DD/YYYY)"
            value={searchBarValue}
            onChange={handleSearch}
            maxLength={10}
          ></input>
          <ul className="intake-history__list">
            {searchedData.map((item) => (
              <IntakeHistoryItem item={item} handleDelete={handleDelete} key={item.id} />
            ))}
          </ul>
          {searchedData.length === 0 ? <div className="intake-history__empty">No intakes found</div> : ""}
          <button
            className="intake-history__button"
            onClick={() => {
              setShowCustomItem(!showCustomItem);
              setTimeout(() => {
                dispatch(
                  setIntakeHistoryHeight(
                    intakeHistoryRef.current != null && intakeHistoryRef.current.getBoundingClientRect().height
                  )
                );
              }, 300);
            }}
          >
            Add a custom item
          </button>
        </div>
        <div
          className={
            showCustomItem
              ? "intake-history__custom-item intake-history__custom-item--active"
              : "intake-history__custom-item"
          }
        >
          <header className="intake-history__custom-item-header">
            <h5 className="intake-history__custom-item-title">Add a custom item</h5>
            <OrangeSeperator toWhite={true} />
            <p className="intake-history__custom-item-description">
              Here you can add a custom item in case you were unable to track nutrition using our web app.
            </p>
          </header>
          <div className="intake-history__custom-item-content">
            <form className="intake-history__custom-item-form">
              <IntakeHistoryInput
                state={[date, setDate]}
                title="Date"
                inputRef={dateRef}
                nextInputRef={amountRef}
                isDate={true}
              />
              <ul className="intake-history__custom-item-nutrition">
                <IntakeHistoryInput
                  state={[amount, setAmount]}
                  title="Amount"
                  unit="g"
                  inputRef={amountRef}
                  nextInputRef={caloriesRef}
                />
                <IntakeHistoryInput
                  state={[calories, setCalories]}
                  title="Calories"
                  unit="cal"
                  inputRef={caloriesRef}
                  nextInputRef={fatRef}
                />
                <IntakeHistoryInput
                  state={[fat, setFat]}
                  title="Fat"
                  unit="g"
                  inputRef={fatRef}
                  nextInputRef={proteinRef}
                />
                <IntakeHistoryInput
                  state={[protein, setProtein]}
                  title="Fat"
                  unit="g"
                  inputRef={proteinRef}
                  nextInputRef={carbsRef}
                />
                <IntakeHistoryInput state={[carbs, setCarbs]} title="Carbs" unit="g" inputRef={carbsRef} />
              </ul>
            </form>
            {errorMessage ? <p className="intake-history__custom-item-error">Please fill out the entire form</p> : ""}
            <button className="intake-history__custom-item-button" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeHistory;
